import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  CloseCircleOutline,
  CheckOutline,
  DropdownOption,
  DropdownInput,
  useToast,
} from '@aslanpay/portal-common-lib';

import { PauseReason } from 'app/constants/individual';

import usePauseIndividual from 'app/hooks/usePauseIndividual';

import { pauseEmployeeSchema } from 'app/utils/schemas';

import { PauseEmployeeFormFields, PauseEmployeeModalProps } from './types';

function PauseEmployeeModal({ id, isOpen, onClose, onPause }: PauseEmployeeModalProps) {
  const toast = useToast();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<PauseEmployeeFormFields>({
    mode: 'onBlur',
    defaultValues: { reason: undefined, comment: '' },
    resolver: yupResolver(pauseEmployeeSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate: pauseEmployee, isLoading: isPausingEmployee } = usePauseIndividual({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Employee account paused',
        description: 'The employee was successfully paused!',
      });

      onPause();
      handleClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the employee account was not paused. Please try again.',
      });
    },
  });

  const handlePauseEmployee = (data: PauseEmployeeFormFields) => {
    pauseEmployee({
      id,
      reason: data.reason,
      comment: data.comment,
    });
  };

  const reasonOptions: DropdownOption[] = Object.keys(PauseReason).map((value) => {
    return {
      value,
      text: PauseReason[value as keyof typeof PauseReason],
    };
  });

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Pause account</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close pause employee"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody mt="20px">
          <form id="pause-employee-form" onSubmit={handleSubmit(handlePauseEmployee)} noValidate>
            <Stack spacing="20px">
              <FormControl isInvalid={!!errors?.reason?.message} isRequired>
                <FormLabel>Reason for pausing account</FormLabel>
                <Controller
                  control={control}
                  name="reason"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DropdownInput
                      placeholder="Select a reason"
                      defaultValue={reasonOptions.find((reason) => reason.value === value)}
                      options={reasonOptions}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  )}
                />
                <FormErrorMessage>{errors?.reason?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors?.comment?.message}>
                <FormLabel>Additional comment</FormLabel>
                <Textarea
                  placeholder="You can add a comment related to the employee"
                  {...register('comment')}
                />
                <FormErrorMessage>{errors?.comment?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Text color="system.error" textStyle="extraSmallMedium">
            * mandatory
          </Text>
          <Spacer />
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            form="pause-employee-form"
            type="submit"
            size="sm"
            rightIcon={<CheckOutline boxSize="20px" />}
            isLoading={isPausingEmployee}
            isDisabled={!isValid}
          >
            Pause account
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default PauseEmployeeModal;
